import React, { useContext, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { LayoutType, ListArtifactItems, SKListArtifact, ContentState } from "../../../models/SKArtifact";
import { AddTextField } from "./container/components/AddTextField";
import { LinkMetadataProviderWidget } from "./container/components/LinkMetadataProviderWidget";
import { LinkImageProvideWidget } from "./container/components/LinkImageProvideWidget";
import { EditLayoutTable } from "./EditLayoutTable";
import { LayoutTableInput } from "./container/components/LayoutTableInput";
import { LayoutCoulmnInput } from "./container/components/LayoutCoulmnInput";
import { LayoutGridInput } from "./container/components/LayoutGridInput";

export const AddLayoutTable = () => {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable, isModerator } = useContext(CreateArtifactContext);
  const [isValidForm, setIsValidForm] = useState(false);
  const [error, setError] = useState<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [items, setItems] = useState<ListArtifactItems[]>([]);
  const [isValidInput, setIsValidInput] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editItem, setEditItem] = useState<ListArtifactItems>();

  const [searchURL, setSearchURL] = useState("");
  const [linkTitle, setLinkTitle] = useState("");
  const [linkPhotoUrl, setLinkPhotoUrl] = useState("");
  const [linkPhotoBase64, setLinkPhotoBase64] = useState("");
  // const [linkId, setLinkId] = useState("");
  const [linkDescription, setLinkDescription] = useState("");
  const [enableAutoSearch, setEnableAutoSearch] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isClear, setIsClear] = useState(false);
  const [layout, setLayout] = useState(LayoutType.table);

  useEffect(() => {
    setLayout((contentData as SKListArtifact).layout);
  }, [contentData]);

  const schema = Yup.object().shape({
    listTitle: Yup.string().required(),
    listDescription: Yup.string().required(),
  });

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate();
  }, [contentData, schema]);

  useEffect(() => {
    if ((contentData as SKListArtifact).listItems != null) {
      setItems((contentData as SKListArtifact).listItems);
    }
  }, []);

  useEffect(() => {
    setIsValidInput(linkTitle.length >= 3);
  }, [linkTitle]);

  const handleSave = async () => {
    setIsEmpty(true);
    try {
      const newItem: ListArtifactItems = {
        id: crypto.randomUUID(),
        listTitle: linkTitle,
        listDescription: linkDescription,
        listImage: linkPhotoUrl,
        listURL: searchURL,
      };

      if (newItem) {
        const updatedItems = [...items, newItem];
        setItems(updatedItems);
        updateContentData({ listItems: updatedItems });
      }

      setIsValidInput(false);
      setLinkTitle("");
      setLinkPhotoUrl("");
      // setLinkId("");
      setLinkDescription("");
      setLinkPhotoBase64("");
      setEnableAutoSearch(false);
      setError("");
      setSearchURL("");
      setIsClear(true);
      setTimeout(() => setIsClear(false), 0);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleRemoveItem = (id: string) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    updateContentData({ listItems: updatedItems });
  };

  const getItemById = (id: string): ListArtifactItems | undefined => {
    return items.find(item => item.id === id);
  };

  const editItemPopup = (id: string) => {
    setEditItem(getItemById(id));
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const savePopup = async (title: string, imageLPath: string) => {
    if (editItem) {
      const updatedItem = {
        ...editItem,
        listTitle: title,
        listImage: imageLPath,
      };
      const updatedItems = items.map(item => (item.id === editItem.id ? updatedItem : item));
      setItems(updatedItems);
      await updateContentData({ listItems: updatedItems });
      closePopup();
    }
  };

  // const addOGTag = async () => {
  //   setIsEmpty(true);
  //   try {
  //     const newItem: ListArtifactItems = {
  //       id: linkId,
  //       listTitle: linkTitle,
  //       listDescription: linkDescription,
  //       listImage: linkPhotoUrl,
  //       listURL: searchURL,
  //     };
  //     if (newItem) {
  //       setItems([...items, newItem]);
  //       setIsValidInput(false);
  //       setLinkTitle("");
  //       setLinkPhotoUrl("");
  //       setLinkId("");
  //       setLinkDescription("");
  //       setLinkPhotoBase64("");
  //       setEnableAutoSearch(false);
  //       setError("");
  //     } else {
  //       alert("Unable to add item. Data Not Found");
  //     }
  //     setSearchURL("");
  //     setIsClear(true);
  //     setTimeout(() => setIsClear(false), 0);
  //   } catch (error) {
  //     console.error("Error fetching user data:", error);
  //   }
  // };

  const parseResult = async (
    data: {
      id: string;
      title: string;
      description: string;
      image: string;
      url: string;
      bufferImage: string;
    },
    actualURL: string
  ) => {
    if (data) {
      // setLinkId(data.id);
      setLinkTitle(data.title);
      setLinkDescription(data.description);
      setLinkPhotoUrl(data.image);
      setLinkPhotoBase64(data.bufferImage);
      setSearchURL(actualURL);
      setIsValidInput(true);
      setEnableAutoSearch(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-[30px] max-w-[520px]">
        {isEditable && !isModerator && (
          <>
            <div>
              <LinkMetadataProviderWidget
                index={1}
                title={"Add URL Link"}
                note={"Type or paste a valid URL link."}
                placeholder={"URL"}
                charAllowed={150}
                isClear={isClear}
                onChange={parseResult}
              />
            </div>
            <div>
              <AddTextField
                name={"linkname"}
                title={"Add Link Name"}
                note={"System will try to auto add the link name from the URL. You can manually add or edit the link name."}
                placeholder={"Link Name"}
                index={2}
                charAllowed={150}
                value={linkTitle}
                onChange={async e => {
                  setLinkTitle(e.target.value);
                }}
                disabled={!isEditable}
              />
            </div>
            <div className="flex flex-col items-center justify-center">
              <LinkImageProvideWidget
                defaultImageUrl={linkPhotoUrl}
                index={3}
                title={"Add Link Photo"}
                information={
                  "System will try to auto add the link photo from the URL. You can manually add or edit the link photo. Recommended aspect ratio of photo is either 4:3, 1:1, or 3:4."
                }
                // Have to check the Document ID // having issue with the folder name while save the image on the firebase store
                documentId={artifactId ?? ""}
                onCoverImageUpload={url => {
                  setLinkPhotoUrl(url);
                }}
                itemsSize={items.length === 0 ? 1 : items.length + 1}
                photoBase64={linkPhotoBase64}
                isClear={isClear}
              />

              <div className="flex w-full  justify-start mt-[14px]">
                <button
                  className={
                    (isValidInput ? " bg-black " : " bg-gray-400  disabled-link ") +
                    "flex items-center justify-center px-3 py-1 text-sm font-normal text-center text-white rounded-lg flex-shrink-0 w-[290px] h-32px"
                  }
                  onClick={handleSave}
                >
                  Add
                </button>
              </div>
            </div>
          </>
        )}

        <div>
          <div className="relative w-full min-w-[200px]">
            <div className="flex items-center">
              {isEditable && !isModerator && <span className="content_count">4</span>}
              <span className="content_title">List Items Added</span>
            </div>
            <div className="notes-class-content">Items you add show up here, you can edit, reorder, and delete.</div>
          </div>
          {layout === LayoutType.coulmn && <LayoutCoulmnInput items={items} editItemPopup={editItemPopup} handleRemoveItem={handleRemoveItem} />}
          {layout === LayoutType.table && <LayoutTableInput items={items} editItemPopup={editItemPopup} handleRemoveItem={handleRemoveItem} />}
          {layout === LayoutType.grid && <LayoutGridInput items={items} editItemPopup={editItemPopup} handleRemoveItem={handleRemoveItem} />}
          {isEditable && isPopupOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-50">
              <div className="relative p-6 bg-white rounded-lg shadow-lg w-96">
                <EditLayoutTable
                  isOpen={isPopupOpen}
                  onClose={closePopup}
                  onSave={savePopup}
                  linkTitle={editItem?.listTitle}
                  linkPhotoUrl={editItem?.listImage}
                  searchURL={editItem?.listURL}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
